import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import cx from "classnames";
import { Message } from "ai/react";
import { useSimulatorChat } from "@/routes/simulator/chat/api";
import { useGetSkills } from "@/v2/entities/skill/useGetSkills";
import { useGetSkillsForRelease } from "@/v2/entities/release/Release.api";
import { RocketIcon } from "@radix-ui/react-icons";
import { Converter } from "showdown";
import {
  Switch,
  Tooltip,
  Button as RadixButton,
  Spinner,
  IconButton,
  Flex,
} from "@radix-ui/themes";
import { translateText } from "../../../services/translation";
import { toast } from "sonner";
import { PiTranslate } from "react-icons/pi";
import dayjs from "dayjs";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  Info,
  AlertTriangle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { TSimulatorLiveModeReviewTicket } from "../types";
import styles from "../styles.module.scss";
import { useInstructions } from "../../simulator/context/instructions";
import { SendInteractionToTraining } from "../sendTicketToTraining";
import { Tools } from "@/routes/simulator/messages";
import { ExploreInteractionRow } from "@/v2/entities/explore/Explore.model";
import { useApi, apiResolver } from "@/api/useApi";
import { useQuery } from "react-query";
import { TicketTranscriptResponse } from "@/v2/features/interaction-view/TicketView";
import {
  ELocalStorage,
  fetchFromLocalStorage,
} from "@/assets/storage/localStorage";
import { useReleases } from "@/routes/simulator/context/release";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { isTicketIncludedInFilterPolicy } from "./lib";
import { MessageComponent } from "@/v2/features/simulator/parts/messages";

export function SimulateTicket({
  ticket,
  activeTicketIndex,
  ticketsLength,
  setActiveTicketIndex,
  topic,
  topicId,
  ticketReview,
  setTicketReview,
  reviewedCount,
  positiveFeedback,
}: {
  ticket: ExploreInteractionRow;
  activeTicketIndex: number;
  ticketsLength: number;
  setActiveTicketIndex: (index: number) => void;
  topic: string | null;
  topicId: string | null;
  ticketReview: {
    ticketId: string;
    good?: boolean;
    comment?: string;
  };
  setTicketReview: (ticketReview: {
    ticketId: string;
    good?: boolean;
    comment?: string;
  }) => void;
  reviewedCount: number;
  positiveFeedback: number;
}) {
  const { releaseId, releases } = useReleases();
  const { instructions } = useInstructions();
  const [customerTranslation, setCustomerTranslation] = useState<string | null>(
    null
  );
  const [botTranslation, setBotTranslation] = useState<string | null>(null);
  const [isTranslatingCustomer, setIsTranslatingCustomer] = useState(false);
  const [isTranslatingBot, setIsTranslatingBot] = useState(false);
  const { data: skills } = useGetSkills();

  const { data: skillsForRelease } = useGetSkillsForRelease(releaseId);

  const skillPayload: Record<string, boolean> = skillsForRelease?.skills
    ? skillsForRelease.skills.reduce(
        (acc, skill) => ({
          ...acc,
          [skill.id]: true,
        }),
        {}
      )
    : {};

  const chatId = React.useMemo(
    () => String(dayjs().valueOf()) + ticket.id,
    [ticket.id]
  );
  const navigate = useNavigate();
  const { simulatorId } = useParams();

  const ticketIds = useMemo(() => {
    const tickets = fetchFromLocalStorage(
      `${ELocalStorage.LIVE_TEST_MODE}-${topicId}`
    );

    return JSON.parse(tickets ?? "[]").map(
      (t: TSimulatorLiveModeReviewTicket) => t.ticketId
    );
  }, []);

  const { messages, append, isLoading, reload } = useSimulatorChat({
    id: chatId,
    isTraining: true,
    instructions: instructions,
    topic: topic ?? "",
    topicId,
    subTopic: "",
    handleResponse: () => {},
    skills: skills?.filter((s) => !!skillPayload[s.id]) ?? [],
    handleError: () => {},
  });

  const filterPolicy = useMemo(() => {
    const filter = releases.find(
      (release) => release.id === releaseId
    )?.filterPolicy;

    if (!filter || (typeof filter === "string" && filter.trim() === "")) {
      return [];
    }

    try {
      return JSON.parse(filter);
    } catch (error) {
      return [];
    }
  }, [releaseId, releases]);

  const isTicketIncluded = useMemo(() => {
    return isTicketIncludedInFilterPolicy(
      { ...ticket, topicId: topicId ?? "" },
      filterPolicy
    );
  }, [ticket, filterPolicy]);

  const ticketReviewRef = useRef<HTMLDivElement | null>(null);

  const [hasAppended, setHasAppended] = useState(false);

  useEffect(() => {
    setHasAppended(false);
    setBotTranslation("");
    setCustomerTranslation("");
  }, [ticket.id]);

  const { api } = useApi();
  const { data: transcript, isLoading: isTranscriptLoading } = useQuery({
    queryKey: ["ticket-view", "transcript", ticket.id],
    queryFn: () =>
      apiResolver<TicketTranscriptResponse>(
        api.get(
          `/platform/tickets/${ticket.id}/comments?includeContextExport=true`
        )
      ),
  });

  const firstComment = React.useMemo(() => {
    const firstCommentRaw = transcript?.comments
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
      .filter((comment) => !comment.isInternal && !comment.agent)?.[0]?.rawBody;

    return firstCommentRaw
      ? `
User Email: ${ticket.tenantUser}
----
${firstCommentRaw}`
      : null;
  }, [transcript]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!hasAppended && firstComment) {
      timeout = setTimeout(() => {
        append({ content: firstComment, role: "user" });
        setHasAppended(true);
      }, 1000);
    }
    return () => clearTimeout(timeout);
  }, [ticket.id, append, firstComment, hasAppended]);

  useEffect(() => {
    if (ticketReviewRef.current) {
      ticketReviewRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const botMessage = React.useMemo(() => {
    return (
      messages.find((message: Message) => message.role === "assistant")
        ?.content || null
    );
  }, [messages]);

  const agentMessage = React.useMemo(() => {
    return messages?.filter(
      (message: Message) => message.role === "assistant"
    )?.[0];
  }, [messages]);

  const tools = agentMessage?.toolInvocations;

  const firstTool = tools?.[0];

  const isResolved =
    firstTool?.state === "result" && firstTool?.result === Tools.RESOLVED;
  const isSkip =
    firstTool?.state === "result" && firstTool?.result === Tools.SKIP_IT;

  const isDontKnow =
    firstTool?.state === "result" && firstTool.result === Tools.DONT_KNOW;

  const isEscalated =
    (firstTool?.state === "result" &&
      firstTool.result === Tools.TALK_TO_AN_AGENT) ||
    (agentMessage?.content === "" && !isSkip && !isResolved && !isDontKnow);

  const OtherTool =
    firstTool?.state === "result" &&
    firstTool.result !== Tools.RESOLVED &&
    firstTool.result !== Tools.SKIP_IT &&
    firstTool.result !== Tools.TALK_TO_AN_AGENT &&
    firstTool.result !== Tools.DONT_KNOW;

  const submitTicketReview = useCallback(
    ({ good, comment }: { good: boolean; comment?: string }) => {
      setTicketReview({
        ticketId: ticket.id,
        good,
        comment,
      });
    },
    [setTicketReview, ticket.id]
  );

  const returnToSimulator = useCallback(() => {
    navigate(`/automation/${simulatorId}`);
  }, [navigate, simulatorId, topicId]);

  const convertor = React.useMemo(
    () =>
      new Converter({
        simplifiedAutoLink: true,
        excludeTrailingPunctuationFromURLs: true,
        openLinksInNewWindow: true,
        smoothLivePreview: true,
        disableForced4SpacesIndentedSublists: true,
      }),
    []
  );

  const convertMarkdownToHTML = useCallback(
    (markdown: string) => {
      if (markdown) {
        return convertor?.makeHtml(markdown);
      } else {
        return "";
      }
    },
    [convertor]
  );

  return (
    <div className={styles.simulateTicketContainer}>
      <div className={styles.topPageContainer}>
        <IconButton
          color="gray"
          variant="ghost"
          size="1"
          onClick={() => {
            returnToSimulator();
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <div className="ml-2">
          <h3>{ticket.subject}</h3>
          <h5>#{ticket.displayId || ticket.id?.split("::")[1]}</h5>
        </div>
      </div>

      <div className={styles.ticketDetailsContainer}>
        <div className={styles.messagesContainer}>
          {isTicketIncluded ? null : (
            <div className="w-full">
              <Alert
                variant="default"
                className="bg-yellow-50 border-yellow-500 gap-2 pl-6"
              >
                <AlertTriangle className="h-6 w-6" />
                <AlertTitle>Heads up:</AlertTitle>
                <AlertDescription>
                  This ticket is currently filtered out by your advanced
                  filters. Review your filter settings to ensure you’re seeing
                  all relevant tickets.
                </AlertDescription>
              </Alert>
            </div>
          )}
          <div className={styles.messageContainer}>
            <p>
              Customer inquiry
              {ticketIds.includes(ticket.id) ? (
                <div className="text-blue-500  bg-blue-100 rounded-md px-2 py-1 text-xs">
                  Already Reviewed
                </div>
              ) : null}
            </p>
            {isTranscriptLoading ? (
              <Spinner size="2" />
            ) : (
              <>
                {customerTranslation || firstComment ? (
                  <div className={cx(styles.message, styles.client)}>
                    <div style={{ width: "100%" }}>
                      {customerTranslation || firstComment || ""}
                    </div>
                    <div className="flex items-center gap-2">
                      <Tooltip
                        content={
                          customerTranslation
                            ? "Show original"
                            : "Translate to English"
                        }
                      >
                        <span>
                          <PiTranslate />
                        </span>
                      </Tooltip>
                      <Switch
                        size="1"
                        disabled={isTranslatingCustomer}
                        checked={!!customerTranslation}
                        onCheckedChange={async (checked) => {
                          if (!checked) {
                            setCustomerTranslation(null);
                            return;
                          }
                          setIsTranslatingCustomer(true);
                          try {
                            const translated = await translateText(
                              firstComment || ""
                            );
                            setCustomerTranslation(translated);
                          } catch (error) {
                            // eslint-disable-next-line no-console
                            console.error("Translation failed:", error);
                            toast.error("Failed to translate message");
                          } finally {
                            setIsTranslatingCustomer(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={`${styles.system} flex gap-3`}>
                    <Info />
                    Agent messaged first, we will skip this conversation
                  </div>
                )}
              </>
            )}
          </div>

          {isLoading ? (
            <div className={styles.messageContainer}>
              <p>
                <RocketIcon /> Generated response
              </p>
              <div className={cx(styles.message, styles.bot)}>
                <Spinner size="2" />
              </div>
            </div>
          ) : (
            agentMessage && (
              <div className={styles.messageContainer}>
                <p>
                  <RocketIcon /> Generated response
                </p>
                <Flex direction="column" gap="4" maxWidth="100%">
                  <MessageComponent
                    role="assistant"
                    content={agentMessage.content}
                    tools={tools}
                    handleEditMessage={() => {}}
                    isLast
                    isLoading={false}
                    sources={[]}
                    trackingData={{}}
                    editDisabled
                  />
                </Flex>
              </div>
            )
          )}
          {ticketReview && (
            <div className={styles.ticketReview} ref={ticketReviewRef}>
              {ticketReview.good === undefined || ticketReview.good === null ? (
                <>
                  <p>Rate the response</p>

                  <div className={styles.ticketReviewButtons}>
                    <SendInteractionToTraining
                      onSubmit={submitTicketReview}
                      interactionId={ticket.id}
                      disableToast
                    />
                    <RadixButton
                      color="gray"
                      variant="solid"
                      size="1"
                      highContrast
                      onClick={() => submitTicketReview({ good: true })}
                    >
                      Good
                    </RadixButton>
                  </div>
                </>
              ) : (
                <div className={styles.submitedFeedback}>
                  {ticketReview.good ? (
                    <p>Good Feedback Submitted!</p>
                  ) : (
                    <p>
                      Feedback submitted! 🎯 This ticket has been added to the
                      training bucket
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={styles.footerContainer}>
        <div className={styles.left}>
          Ticket {activeTicketIndex + 1} out of {ticketsLength}
          <br />
          {reviewedCount} reviewed ({positiveFeedback} positive feedback)
        </div>
        <div className={styles.right + " flex items-center gap-2"}>
          <RadixButton
            color="gray"
            variant="outline"
            highContrast
            className={styles.nextTicket}
            disabled={activeTicketIndex === 0}
            onClick={useCallback(
              () => setActiveTicketIndex(activeTicketIndex - 1),
              [activeTicketIndex, setActiveTicketIndex]
            )}
          >
            <ArrowLeftIcon />
            Previous Ticket
          </RadixButton>
          <RadixButton
            color="gray"
            variant="outline"
            highContrast
            className={styles.nextTicket}
            onClick={useCallback(
              () =>
                activeTicketIndex !== ticketsLength - 1
                  ? setActiveTicketIndex(activeTicketIndex + 1)
                  : returnToSimulator(),
              [
                activeTicketIndex,
                ticketsLength,
                setActiveTicketIndex,
                returnToSimulator,
              ]
            )}
          >
            {activeTicketIndex === ticketsLength - 1 ? "Done" : "Next Ticket"}
            <ArrowRightIcon />
          </RadixButton>
        </div>
      </div>
    </div>
  );
}
